/**
 * Top bar: carosello su mobile.
 *
 * Sotto il breakpoint lg le colonne left/right sono nascoste dal tema
 * (.hidden nel markup). Con il carosello attivo il CSS le porta a
 * display:contents, cosi' i blocchi diventano figli diretti della striscia
 * scorrevole senza duplicare il DOM: nessun id ripetuto e nessun form
 * doppio per il selettore lingua/paese.
 *
 * Qui restano solo i comportamenti che il CSS non copre: mostrare le frecce
 * quando il contenuto sborda davvero, e lo scorrimento automatico opzionale.
 */
if (!customElements.get('topbar-carousel')) {
  customElements.define(
    'topbar-carousel',
    class TopbarCarousel extends HTMLElement {
      connectedCallback() {
        this.strip = this.querySelector('.topbar__inner');
        if (!this.strip) return;

        this.prevButton = this.querySelector('.topbar__nav--prev');
        this.nextButton = this.querySelector('.topbar__nav--next');
        this.autoplayDelay = parseInt(this.dataset.autoplay, 10) || 0;
        this.mediaQuery = window.matchMedia('(max-width: 1023.98px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.update = this.update.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);

        this.strip.addEventListener('scroll', this.update, { passive: true });
        this.prevButton?.addEventListener('click', () => this.step(-1));
        this.nextButton?.addEventListener('click', () => this.step(1));

        // l'autoplay non deve contrastare chi sta guardando o interagendo
        ['pointerdown', 'focusin', 'mouseenter'].forEach((event) =>
          this.addEventListener(event, () => this.stopAutoplay(), { passive: true })
        );
        this.addEventListener('mouseleave', () => this.update(), { passive: true });
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(this.update);
          this.resizeObserver.observe(this.strip);
        } else {
          window.addEventListener('resize', this.update);
        }

        /* Fermare l'autoplay quando la barra e' fuori schermo: non serve
           animare cio' che nessuno vede, e toglie di mezzo una classe di
           effetti collaterali sullo scorrimento della pagina. */
        if (window.IntersectionObserver) {
          this.visible = true;
          this.intersectionObserver = new IntersectionObserver((entries) => {
            this.visible = entries[0].isIntersecting;
            this.update();
          });
          this.intersectionObserver.observe(this);
        }
        this.mediaQuery.addEventListener('change', this.update);

        this.update();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.strip?.removeEventListener('scroll', this.update);
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        window.removeEventListener('resize', this.update);
        this.mediaQuery?.removeEventListener('change', this.update);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }

      /* Il margine di 1px assorbe gli arrotondamenti dei layout frazionari,
         altrimenti le frecce comparirebbero anche quando tutto entra. */
      get isOverflowing() {
        return this.strip.scrollWidth - this.strip.clientWidth > 1;
      }

      get isActive() {
        return this.mediaQuery.matches && this.isOverflowing;
      }

      /* undefined quando IntersectionObserver non c'e': in quel caso
         l'autoplay parte comunque, come prima */
      get isVisible() {
        return this.visible !== false;
      }

      update() {
        const active = this.isActive;
        const scrollLeft = Math.round(this.strip.scrollLeft);
        const maxScroll = this.strip.scrollWidth - this.strip.clientWidth;

        if (this.prevButton) {
          this.prevButton.hidden = !active;
          this.prevButton.disabled = scrollLeft <= 0;
        }
        if (this.nextButton) {
          this.nextButton.hidden = !active;
          this.nextButton.disabled = scrollLeft >= maxScroll - 1;
        }

        this.classList.toggle('topbar__carousel--active', active);

        if (active && this.isVisible) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      }

      get items() {
        return Array.from(this.strip.querySelectorAll('.topbar__column > *'));
      }

      /**
       * Distanza di un elemento dal bordo iniziale della striscia lungo l'asse
       * di scorrimento. E' l'unico punto che deve sapere della direzione:
       * ordinando per questo valore si ottiene l'ordine visivo sia in LTR
       * che in RTL, e tutto il resto puo' ignorare il problema.
       */
      inlineStart(rect, stripRect, rtl) {
        return rtl ? stripRect.right - rect.right : rect.left - stripRect.left;
      }

      /* Avanza di un elemento per volta, non di una frazione di viewport:
         il blocco successivo viene allineato al bordo iniziale. */
      step(direction) {
        const styles = getComputedStyle(this.strip);
        const rtl = styles.direction === 'rtl';
        const padding = parseFloat(styles.scrollPaddingInlineStart) || 0;
        const stripRect = this.strip.getBoundingClientRect();

        const ordered = this.items
          .map((element) => ({ element, offset: this.inlineStart(element.getBoundingClientRect(), stripRect, rtl) }))
          .sort((a, b) => a.offset - b.offset);

        const target =
          direction > 0
            ? ordered.find(({ offset }) => offset > padding + 1)
            : [...ordered].reverse().find(({ offset }) => offset < padding - 1);

        if (!target) return;

        /* scrollBy sulla striscia e NON scrollIntoView sull'elemento:
           scrollIntoView scorre tutti gli antenati scrollabili, documento
           compreso, quindi con la top bar fuori schermo riportava l'intera
           pagina in cima a ogni tick dell'autoplay. scrollBy tocca solo
           l'elemento su cui e' chiamato.
           Il delta e' fisico e ricavato dai rect, quindi resta corretto anche
           in RTL senza dipendere da come il browser conta scrollLeft. */
        const targetRect = target.element.getBoundingClientRect();
        const delta = rtl
          ? targetRect.right - (stripRect.right - padding)
          : targetRect.left - (stripRect.left + padding);

        this.strip.scrollBy({
          left: delta,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      startAutoplay() {
        if (!this.autoplayDelay || this.timer || this.reducedMotion.matches) return;

        this.timer = setInterval(() => {
          const maxScroll = this.strip.scrollWidth - this.strip.clientWidth;
          if (Math.abs(Math.round(this.strip.scrollLeft)) >= maxScroll - 1) {
            this.strip.scrollTo({ left: 0, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
          } else {
            this.step(1);
          }
        }, this.autoplayDelay);
      }

      stopAutoplay() {
        if (!this.timer) return;
        clearInterval(this.timer);
        this.timer = null;
      }

      onVisibilityChange() {
        document.hidden ? this.stopAutoplay() : this.update();
      }
    }
  );
}
